import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  useColorModeValue,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
  Alert,
  AlertIcon,
  AlertDescription,
  Badge,
  SimpleGrid,
  ButtonGroup,
  InputGroup,
  InputRightElement,
  Select,
} from "@chakra-ui/react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import PageShell from "../layout/PageShell";
import { LogoCatalogModal } from "./LogoCatalog";
import { LogoFile } from "./logoSeries";
import {
  POS_RECEIPT_EMAIL_COLLECTION,
  POS_RECEIPT_EMAIL_DOC_ID,
  DEFAULT_POS_RECEIPT_TEMPLATE,
  DEFAULT_POS_RECEIPT_SHELL,
  SAMPLE_RECEIPT_BODY,
  PosReceiptEmailTemplate,
  fillPosReceiptShell,
} from "./posReceiptEmailDefaults";

const SES_CONFIG_COLLECTION = "sesConfig";
const SES_CONFIG_DOC_ID = "global";

const SES_REGIONS = [
  "ca-central-1",
  "us-east-1",
  "us-east-2",
  "us-west-1",
  "us-west-2",
  "eu-west-1",
  "eu-west-2",
  "eu-west-3",
  "eu-central-1",
];

const PosReceiptEmailManagement: React.FC = () => {
  const toast = useToast();
  const { currentUser } = useAuth();
  const [template, setTemplate] = useState<PosReceiptEmailTemplate>(DEFAULT_POS_RECEIPT_TEMPLATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const previewLang = "fr" as const;
  const [previewWidth, setPreviewWidth] = useState<600 | 375>(600);
  const [logoOpen, setLogoOpen] = useState(false);
  const [testAddress, setTestAddress] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const [sesSaving, setSesSaving] = useState(false);
  const [sesRegion, setSesRegion] = useState("ca-central-1");
  const [sesAccessKeyId, setSesAccessKeyId] = useState("");
  const [sesSecret, setSesSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [sesFromAddress, setSesFromAddress] = useState("");
  const [sesFromName, setSesFromName] = useState("NuagePOS");
  const [sesReplyTo, setSesReplyTo] = useState("");

  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.700", "white");
  const textSecondary = useColorModeValue("gray.500", "gray.400");

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, POS_RECEIPT_EMAIL_COLLECTION, POS_RECEIPT_EMAIL_DOC_ID));
        if (snap.exists()) {
          setTemplate({ ...DEFAULT_POS_RECEIPT_TEMPLATE, ...(snap.data() as Partial<PosReceiptEmailTemplate>) });
        }
      } catch (error: any) {
        toast({ title: "Unable to load template", description: error.message, status: "error", duration: 5000 });
      } finally {
        setLoading(false);
      }
    };
    const loadSes = async () => {
      try {
        const snap = await getDoc(doc(db, SES_CONFIG_COLLECTION, SES_CONFIG_DOC_ID));
        const data = snap.exists() ? snap.data() : {};
        if (data.region) setSesRegion(data.region);
        setSesAccessKeyId(data.accessKeyId || "");
        setSesSecret(data.secretAccessKey || "");
        setSesFromAddress(data.fromAddress || "");
        setSesFromName(data.fromName || "NuagePOS");
        setSesReplyTo(data.replyTo || "");
      } catch (error: any) {
        toast({ title: "Unable to load Amazon SES settings", description: error.message, status: "error", duration: 5000 });
      }
    };
    load();
    loadSes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sesConfigured = Boolean(sesRegion && sesAccessKeyId.trim() && sesSecret.trim() && sesFromAddress.trim());

  const update = (field: keyof PosReceiptEmailTemplate, value: string) =>
    setTemplate((prev) => ({ ...prev, [field]: value }));

  const shellIsValid = template.htmlShell.includes("{{receiptBody}}");

  const handleSave = async () => {
    if (!shellIsValid) {
      toast({
        title: "Template not saved",
        description: "The HTML shell must contain {{receiptBody}}.",
        status: "error",
        duration: 5000,
      });
      return;
    }
    setSaving(true);
    try {
      await setDoc(
        doc(db, POS_RECEIPT_EMAIL_COLLECTION, POS_RECEIPT_EMAIL_DOC_ID),
        { ...template, updatedAt: serverTimestamp(), updatedBy: currentUser?.email || "" },
        { merge: true }
      );
      toast({ title: "Template saved", status: "success", duration: 3000 });
    } catch (error: any) {
      toast({ title: "Template not saved", description: error.message, status: "error", duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const previewHtml = useMemo(
    () => fillPosReceiptShell(template, previewLang, SAMPLE_RECEIPT_BODY, "Entreprise ABC"),
    [template, previewLang]
  );

  const previewSubject = useMemo(
    () =>
      template.subjectFormat
        .replace(/\{\{\s*receiptLabel\s*\}\}/g, previewLang === "fr" ? "Reçu" : "Receipt")
        .replace(/\{\{\s*storeName\s*\}\}/g, "Entreprise ABC")
        .replace(/\{\{\s*receiptNumber\s*\}\}/g, "000123"),
    [template.subjectFormat, previewLang]
  );

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const callable = httpsCallable<any, { ok: boolean; code?: string; message?: string }>(functions, "sendPosReceiptEmail");
      const { data } = await callable({
        testMode: true,
        to: testAddress.trim(),
        subject: `[Test] ${previewSubject}`,
        html: previewHtml,
        storeName: "Entreprise ABC",
      });
      if (data.ok) {
        toast({ title: "Test email sent", description: testAddress.trim(), status: "success", duration: 4000 });
      } else {
        toast({ title: "Test email not sent", description: `${data.code}: ${data.message}`, status: "error", duration: 6000 });
      }
    } catch (error: any) {
      toast({ title: "Test email not sent", description: error.message, status: "error", duration: 6000 });
    } finally {
      setSendingTest(false);
    }
  };

  const handleSaveSes = async () => {
    setSesSaving(true);
    try {
      await setDoc(
        doc(db, SES_CONFIG_COLLECTION, SES_CONFIG_DOC_ID),
        {
          region: sesRegion,
          accessKeyId: sesAccessKeyId.trim(),
          secretAccessKey: sesSecret.trim(),
          fromAddress: sesFromAddress.trim().toLowerCase(),
          fromName: sesFromName.trim(),
          replyTo: sesReplyTo.trim().toLowerCase(),
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || "",
        },
        { merge: true }
      );
      toast({ title: "Amazon SES settings saved", status: "success", duration: 3000 });
    } catch (error: any) {
      toast({ title: "Amazon SES settings not saved", description: error.message, status: "error", duration: 6000 });
    } finally {
      setSesSaving(false);
    }
  };

  const sectionBox = {
    bg: bgCard,
    borderRadius: "lg",
    boxShadow: "sm",
    border: "1px",
    borderColor,
    p: 6,
  } as const;

  return (
    <PageShell
      title="POS Receipt Email"
      description="Branded email that carries the electronic receipt issued at the point of sale. This template is separate from the online order email templates."
      wide
    >
      <VStack spacing={6} align="stretch">

        <Tabs variant="enclosed" isLazy>
          <TabList>
            <Tab>Branding</Tab>
            <Tab>HTML shell</Tab>
            <Tab>Preview</Tab>
            <Tab>Test send</Tab>
            <Tab>
              Amazon SES
              <Badge ml={2} colorScheme={sesConfigured ? "green" : "orange"}>
                {sesConfigured ? "Configured" : "Not configured"}
              </Badge>
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <VStack spacing={6} align="stretch" {...sectionBox}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <FormControl>
                    <FormLabel>Logo URL</FormLabel>
                    <HStack>
                      <Input value={template.logoUrl} onChange={(e) => update("logoUrl", e.target.value)} bg="white" />
                      <Button flexShrink={0} onClick={() => setLogoOpen(true)}>Choose logo</Button>
                    </HStack>
                    <FormHelperText>Displayed in the header next to « Propulsé par NuagePOS ». Save the template after you choose a logo.</FormHelperText>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Brand color</FormLabel>
                    <HStack>
                      <Input
                        type="color"
                        w="64px"
                        p={1}
                        value={template.brandColor}
                        onChange={(e) => update("brandColor", e.target.value)}
                      />
                      <Input value={template.brandColor} onChange={(e) => update("brandColor", e.target.value)} bg="white" />
                    </HStack>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Heading</FormLabel>
                    <Input value={template.headingFr} onChange={(e) => update("headingFr", e.target.value)} bg="white" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Footer</FormLabel>
                    <Textarea value={template.footerFr} onChange={(e) => update("footerFr", e.target.value)} bg="white" rows={3} />
                  </FormControl>
                </SimpleGrid>
                <FormControl>
                  <FormLabel>Subject format</FormLabel>
                  <Input value={template.subjectFormat} onChange={(e) => update("subjectFormat", e.target.value)} bg="white" />
                  <FormHelperText>
                    Available values: {"{{receiptLabel}}"}, {"{{storeName}}"}, {"{{receiptNumber}}"}. Preview: {previewSubject}
                  </FormHelperText>
                </FormControl>
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    The legal receipt zone (Courriel, Copie électronique, QR code, and "Consulter la transaction en ligne") is
                    generated by the POS and cannot be edited here.
                  </AlertDescription>
                </Alert>
                <Button alignSelf="flex-start" onClick={handleSave} isLoading={saving || loading}>
                  Save template
                </Button>
                <LogoCatalogModal
                  isOpen={logoOpen}
                  onClose={() => setLogoOpen(false)}
                  selectedUrl={template.logoUrl}
                  hint="This fills the Logo URL field. Save the template to keep it."
                  onSelect={(file: LogoFile) => update("logoUrl", file.publicUrl)}
                />
              </VStack>
            </TabPanel>

            <TabPanel px={0}>
              <VStack spacing={4} align="stretch" {...sectionBox}>
                {!shellIsValid && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>The HTML shell must contain {"{{receiptBody}}"}.</AlertDescription>
                  </Alert>
                )}
                <FormControl>
                  <FormLabel>HTML shell</FormLabel>
                  <Textarea
                    value={template.htmlShell}
                    onChange={(e) => update("htmlShell", e.target.value)}
                    bg="white"
                    fontFamily="mono"
                    fontSize="sm"
                    minH="480px"
                  />
                  <FormHelperText>
                    Values: {"{{lang}}"}, {"{{brandColor}}"}, {"{{logoUrl}}"}, {"{{heading}}"}, {"{{storeName}}"},{" "}
                    {"{{receiptBody}}"}, {"{{footer}}"}, {"{{poweredByUrl}}"}.
                  </FormHelperText>
                </FormControl>
                <HStack>
                  <Button onClick={handleSave} isLoading={saving}>
                    Save template
                  </Button>
                  <Button variant="outline" onClick={() => update("htmlShell", DEFAULT_POS_RECEIPT_SHELL)}>
                    Restore default shell
                  </Button>
                </HStack>
              </VStack>
            </TabPanel>

            <TabPanel px={0}>
              <VStack spacing={4} align="stretch" {...sectionBox}>
                <HStack justify="space-between" wrap="wrap" gap={3}>
                  <Text fontSize="sm" color={textSecondary}>
                    Subject: <strong>{previewSubject}</strong>
                  </Text>
                  <HStack>
                    <ButtonGroup isAttached size="md">
                      <Button onClick={() => setPreviewWidth(600)} colorScheme={previewWidth === 600 ? "blue" : "gray"}>
                        Desktop 600px
                      </Button>
                      <Button onClick={() => setPreviewWidth(375)} colorScheme={previewWidth === 375 ? "blue" : "gray"}>
                        Mobile 375px
                      </Button>
                    </ButtonGroup>
                  </HStack>
                </HStack>
                <Box bg="gray.100" borderRadius="md" p={4} display="flex" justifyContent="center">
                  <iframe
                    srcDoc={previewHtml}
                    title="POS receipt email preview"
                    style={{ width: `${previewWidth}px`, height: "900px", border: "1px solid #d1d5db", background: "white" }}
                  />
                </Box>
              </VStack>
            </TabPanel>

            <TabPanel px={0}>
              <VStack spacing={4} align="stretch" {...sectionBox} maxW="640px">
                {!sesConfigured && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>Amazon SES is not configured. Complete the Amazon SES tab first.</AlertDescription>
                  </Alert>
                )}
                <Text fontSize="sm" color={textSecondary}>
                  Sends the current preview with sample data. Test emails are not copied to any merchant.
                </Text>
                <FormControl isRequired>
                  <FormLabel>Recipient email</FormLabel>
                  <Input
                    type="email"
                    value={testAddress}
                    onChange={(e) => setTestAddress(e.target.value)}
                    placeholder="recipient@example.com"
                    bg="white"
                  />
                </FormControl>
                <Button
                  alignSelf="flex-start"
                  colorScheme="green"
                  onClick={handleSendTest}
                  isLoading={sendingTest}
                  isDisabled={!testAddress.trim() || !sesConfigured}
                >
                  Send test email
                </Button>
              </VStack>
            </TabPanel>

            <TabPanel px={0}>
              <VStack spacing={5} align="stretch" {...sectionBox} maxW="720px">
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    Credentials are readable only by the super administrator account. Use the Test send tab to confirm
                    the connection after saving.
                  </AlertDescription>
                </Alert>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                  <FormControl>
                    <FormLabel>Region</FormLabel>
                    <Select value={sesRegion} onChange={(e) => setSesRegion(e.target.value)} bg="white" size="md">
                      {SES_REGIONS.map((region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Access key ID</FormLabel>
                    <Input
                      size="md"
                      value={sesAccessKeyId}
                      onChange={(e) => setSesAccessKeyId(e.target.value)}
                      placeholder="AKIA..."
                      bg="white"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Secret access key</FormLabel>
                    <InputGroup size="md">
                      <Input
                        type={showSecret ? "text" : "password"}
                        value={sesSecret}
                        onChange={(e) => setSesSecret(e.target.value)}
                        bg="white"
                        autoComplete="new-password"
                      />
                      <InputRightElement width="5rem">
                        <Button h="2rem" size="sm" onClick={() => setShowSecret((v) => !v)}>
                          {showSecret ? "Hide" : "Show"}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Sender address</FormLabel>
                    <Input
                      size="md"
                      type="email"
                      value={sesFromAddress}
                      onChange={(e) => setSesFromAddress(e.target.value)}
                      placeholder="receipts@nuagepos.com"
                      bg="white"
                    />
                    <FormHelperText>Must be a verified identity in Amazon SES.</FormHelperText>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Sender name</FormLabel>
                    <Input size="md" value={sesFromName} onChange={(e) => setSesFromName(e.target.value)} bg="white" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Reply-to address</FormLabel>
                    <Input
                      size="md"
                      type="email"
                      value={sesReplyTo}
                      onChange={(e) => setSesReplyTo(e.target.value)}
                      placeholder="Store email is used when empty"
                      bg="white"
                    />
                  </FormControl>
                </SimpleGrid>
                <Button
                  colorScheme="blue"
                  size="md"
                  onClick={handleSaveSes}
                  isLoading={sesSaving}
                  isDisabled={!sesConfigured}
                  alignSelf="flex-start"
                >
                  Save Amazon SES settings
                </Button>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </PageShell>
  );
};

export default PosReceiptEmailManagement;
